import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, CheckCheck, CheckCircle2, Download, PlayCircle, RefreshCw, ShieldAlert,
  Sparkles, Search, Loader2, X, Lock, AlertCircle,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import SiteCombobox from '../components/SiteCombobox';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import api from '../services/api';

// ── Status badge styles ───────────────────────────────────────────────────────
const STATUS_STYLE = {
  exact: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  strong_suggestion: 'bg-blue-50 text-blue-700 border-blue-200',
  review_required: 'bg-amber-50 text-amber-700 border-amber-200',
  proposed_new: 'bg-purple-50 text-purple-700 border-purple-200',
  blocked: 'bg-red-50 text-red-700 border-red-200',
  pending: 'bg-slate-100 text-slate-600 border-slate-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
  applied: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  open: 'bg-amber-50 text-amber-700 border-amber-200',
};

function StatusBadge({ status }) {
  if (!status) return <span className="text-slate-400 text-xs">—</span>;
  return (
    <Badge
      variant="outline"
      className={`text-xs font-medium ${STATUS_STYLE[status] || 'bg-slate-100 text-slate-600 border-slate-200'}`}
      data-testid={`status-badge-${status}`}
    >
      {status.replace(/_/g, ' ')}
    </Badge>
  );
}

// ── Category definitions ──────────────────────────────────────────────────────
const CATEGORY_TABS = [
  { key: 'match',       label: 'Product Matches',        countKey: 'match' },
  { key: 'new_product', label: 'New Product Proposals',  countKey: 'new_product' },
  { key: 'postmix',     label: 'Post-Mix Exceptions',    countKey: 'postmix' },
  { key: 'price',       label: 'Site Price Mappings',    countKey: 'price' },
];

const STATUS_OPTIONS = {
  match:       ['exact', 'strong_suggestion', 'review_required', 'proposed_new', 'blocked'],
  new_product: ['pending', 'approved', 'rejected', 'applied'],
  postmix:     ['open', 'resolved'],
  price:       ['pending', 'approved', 'rejected', 'blocked'],
};

// ── Category-specific table headers ──────────────────────────────────────────
function TableHeader({ category, allSelected, onToggleAll }) {
  const Sel = () => (
    <th className="py-2 pr-2 w-8">
      <input
        type="checkbox"
        checked={allSelected}
        onChange={onToggleAll}
        data-testid="select-all-checkbox"
      />
    </th>
  );
  if (category === 'match') return (
    <tr className="border-b text-left text-xs text-slate-500 uppercase tracking-wide">
      <Sel />
      <th className="py-2 pr-4 font-semibold">Legacy Code</th>
      <th className="py-2 pr-4 font-semibold">Description</th>
      <th className="py-2 pr-4 font-semibold">Group</th>
      <th className="py-2 pr-4 font-semibold">Match Status</th>
      <th className="py-2 pr-4 font-semibold">Decision</th>
      <th className="py-2 pr-4 font-semibold">Conf.</th>
      <th className="py-2 pr-4 font-semibold">Matched Product</th>
      <th className="py-2 pr-4 font-semibold">Evidence / AI</th>
    </tr>
  );
  if (category === 'new_product') return (
    <tr className="border-b text-left text-xs text-slate-500 uppercase tracking-wide">
      <Sel />
      <th className="py-2 pr-4 font-semibold">Legacy Code</th>
      <th className="py-2 pr-4 font-semibold">Description</th>
      <th className="py-2 pr-4 font-semibold">Proposed Name</th>
      <th className="py-2 pr-4 font-semibold">Proposed Code</th>
      <th className="py-2 pr-4 font-semibold">Group</th>
      <th className="py-2 pr-4 font-semibold">Status</th>
      <th className="py-2 pr-4 font-semibold">Pack / Type</th>
    </tr>
  );
  if (category === 'postmix') return (
    <tr className="border-b text-left text-xs text-slate-500 uppercase tracking-wide">
      <Sel />
      <th className="py-2 pr-4 font-semibold">Legacy Code</th>
      <th className="py-2 pr-4 font-semibold">Description</th>
      <th className="py-2 pr-4 font-semibold">Group</th>
      <th className="py-2 pr-4 font-semibold">Status</th>
      <th className="py-2 pr-4 font-semibold">Exception / Workbook Candidate</th>
    </tr>
  );
  // price
  return (
    <tr className="border-b text-left text-xs text-slate-500 uppercase tracking-wide">
      <Sel />
      <th className="py-2 pr-4 font-semibold">Legacy Code</th>
      <th className="py-2 pr-4 font-semibold">Description</th>
      <th className="py-2 pr-4 font-semibold">BARPRICE</th>
      <th className="py-2 pr-4 font-semibold">Measure</th>
      <th className="py-2 pr-4 font-semibold">Mapped Price</th>
      <th className="py-2 pr-4 font-semibold">Status</th>
      <th className="py-2 pr-4 font-semibold">Blocked Reason</th>
    </tr>
  );
}

// ── Category-specific table rows ──────────────────────────────────────────────
function TableRow({ category, it, selected, onToggle, onAiSuggest, aiLoading }) {
  const CheckCell = () => (
    <td className="py-2 pr-2">
      <input
        type="checkbox"
        checked={selected}
        onChange={() => onToggle(it.id)}
        data-testid={`row-checkbox-${it.id}`}
      />
    </td>
  );

  if (category === 'match') return (
    <tr className="border-b last:border-0 hover:bg-slate-50" data-testid={`finding-row-${it.id}`}>
      <CheckCell />
      <td className="py-2 pr-4 font-mono text-xs">{it.legacy_item_code}</td>
      <td className="py-2 pr-4 text-sm">{it.description}</td>
      <td className="py-2 pr-4 text-xs text-slate-500">{it.group_name}</td>
      <td className="py-2 pr-4"><StatusBadge status={it.match_status} /></td>
      <td className="py-2 pr-4"><StatusBadge status={it.decision} /></td>
      <td className="py-2 pr-4 text-xs tabular-nums">
        {it.confidence != null ? Number(it.confidence).toFixed(2) : '—'}
      </td>
      <td className="py-2 pr-4 text-xs">{it.matched_name || '—'}</td>
      <td className="py-2 pr-4 text-xs text-slate-500 max-w-[260px]">
        {it.ai_suggestion ? (
          <span
            className="flex items-start gap-1 text-purple-700"
            data-testid={`ai-suggestion-${it.id}`}
          >
            <Sparkles className="w-3 h-3 mt-0.5 shrink-0" /> {it.ai_suggestion}
          </span>
        ) : (
          <>
            <span>
              {Array.isArray(it.reasons) ? it.reasons.join('; ') : (it.reasons || '—')}
            </span>
            {it.match_status === 'review_required' && (
              <button
                className="ml-1 inline-flex items-center gap-0.5 text-purple-600 hover:text-purple-800 text-xs underline"
                onClick={() => onAiSuggest(it.id)}
                disabled={aiLoading}
                data-testid={`ai-suggest-btn-${it.id}`}
              >
                {aiLoading
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : <Sparkles className="w-3 h-3" />}
                Suggest
              </button>
            )}
          </>
        )}
      </td>
    </tr>
  );

  if (category === 'new_product') return (
    <tr className="border-b last:border-0 hover:bg-slate-50" data-testid={`finding-row-${it.id}`}>
      <CheckCell />
      <td className="py-2 pr-4 font-mono text-xs">{it.legacy_item_code}</td>
      <td className="py-2 pr-4 text-sm">{it.description}</td>
      <td className="py-2 pr-4 text-sm">{it.proposed_name || '—'}</td>
      <td className="py-2 pr-4 font-mono text-xs">{it.proposed_code || '—'}</td>
      <td className="py-2 pr-4 text-xs text-slate-500">{it.group_name}</td>
      <td className="py-2 pr-4"><StatusBadge status={it.status} /></td>
      <td className="py-2 pr-4 text-xs">
        {it.is_post_mix
          ? 'Post-mix'
          : it.purchase_unit_ml != null
            ? `${it.purchase_unit_ml} ml`
            : '—'}
      </td>
    </tr>
  );

  if (category === 'postmix') return (
    <tr className="border-b last:border-0 hover:bg-slate-50" data-testid={`finding-row-${it.id}`}>
      <CheckCell />
      <td className="py-2 pr-4 font-mono text-xs">{it.legacy_item_code}</td>
      <td className="py-2 pr-4 text-sm">{it.description}</td>
      <td className="py-2 pr-4 text-xs text-slate-500">{it.group_name}</td>
      <td className="py-2 pr-4"><StatusBadge status={it.status} /></td>
      <td className="py-2 pr-4 text-xs text-slate-500 max-w-[280px]">
        {it.workbook_candidate || it.reason || '—'}
      </td>
    </tr>
  );

  // price
  return (
    <tr className="border-b last:border-0 hover:bg-slate-50" data-testid={`finding-row-${it.id}`}>
      <CheckCell />
      <td className="py-2 pr-4 font-mono text-xs">{it.legacy_item_code}</td>
      <td className="py-2 pr-4 text-sm">{it.description}</td>
      {/* API field is legacy_barprice */}
      <td className="py-2 pr-4 text-xs tabular-nums">
        {it.legacy_barprice != null ? `£${Number(it.legacy_barprice).toFixed(2)}` : '—'}
      </td>
      <td className="py-2 pr-4 text-xs">
        {it.measure_label || (it.measure_ml ? `${it.measure_ml}ml` : '—')}
      </td>
      <td className="py-2 pr-4 text-xs tabular-nums">
        {it.mapped_price != null ? `£${Number(it.mapped_price).toFixed(2)}` : '—'}
      </td>
      <td className="py-2 pr-4"><StatusBadge status={it.status} /></td>
      <td className="py-2 pr-4 text-xs text-slate-500 max-w-[200px]">
        {it.blocked_reason || '—'}
      </td>
    </tr>
  );
}

const COL_COUNT = { match: 9, new_product: 8, postmix: 6, price: 8 };

// ── Page ──────────────────────────────────────────────────────────────────────
export default function PsbStage1Page() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // URL-pinned run (set when navigating from the historical conversion wizard)
  const urlRunId  = searchParams.get('run_id');
  const urlSiteId = searchParams.get('site_id');
  const [pinnedRunId,    setPinnedRunId]    = useState(urlRunId);
  const [pinnedRunError, setPinnedRunError] = useState(null);
  const [pinnedRunLoading, setPinnedRunLoading] = useState(!!urlRunId);

  // Setup
  const [sources, setSources] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedSource, setSelectedSource] = useState('');
  const [selectedSite, setSelectedSite] = useState('');
  const [scanning, setScanning] = useState(false);
  const [isProduction, setIsProduction] = useState(false);

  // Run state
  const [run, setRun] = useState(null);
  const [polling, setPolling] = useState(false);

  // Findings
  const [category, setCategory] = useState('match');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [findings, setFindings] = useState({ items: [], total: 0 });
  const [loadingFindings, setLoadingFindings] = useState(false);
  const [selected, setSelected] = useState(new Set());
  // Version counter — increment to force a findings refresh without changing other deps
  const [findingsVersion, setFindingsVersion] = useState(0);

  // Actions
  const [dryRunResult, setDryRunResult] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(new Set());

  // ── Pinned run: load directly from URL param, fail closed on any mismatch ──
  useEffect(() => {
    if (!urlRunId) return;
    setPinnedRunLoading(true);
    api.get(`/admin/psb-stage1/runs/${urlRunId}`)
      .then(r => {
        const loaded = r.data;
        // Site isolation: if a site_id guard was supplied, the run must match it exactly
        if (urlSiteId && String(loaded.target_site_id) !== String(urlSiteId)) {
          setPinnedRunError(
            `Run ${urlRunId.slice(0, 8)} belongs to site ${loaded.target_site_id}` +
            (loaded.target_site_name ? ` (${loaded.target_site_name})` : '') +
            `, not the expected site ${urlSiteId}. ` +
            'No data has been loaded. Return to the historical conversion page and use the link there.'
          );
          return;
        }
        setRun(loaded);
        setPinnedRunId(urlRunId);
        if (loaded.status === 'scanning') setPolling(true);
      })
      .catch(e => {
        const detail = e?.response?.data?.detail || 'not found or not accessible';
        setPinnedRunError(
          `Run ${urlRunId.slice(0, 8)} could not be loaded: ${detail}. ` +
          'No data has been loaded. Return to the historical conversion page and use the link there.'
        );
      })
      .finally(() => setPinnedRunLoading(false));
  }, []); // urlRunId and urlSiteId are URL-fixed at mount — stable for lifecycle

  // ── Load setup data ────────────────────────────────────────────────────────
  useEffect(() => {
    api.get('/admin/psb-stage1/stage0-sources')
      .then(r => setSources(r.data?.sources || []))
      .catch(() => toast.error('Failed to load Stage 0 sources'));

    api.get('/admin/psb-stage1/sites').then(r => {
      const list = r.data?.sites || [];
      setSites(list);
      if (list.length === 1) setSelectedSite(String(list[0].id));
    }).catch(() => toast.error('Failed to load sites'));

    api.get('/admin/product-master/environment')
      .then(r => setIsProduction(!!r.data?.is_production))
      .catch(() => {});
  }, []);

  const selectedSiteObj = sites.find(s => String(s.id) === String(selectedSite));

  // ── Source label (disambiguation) ─────────────────────────────────────────
  const sourceLabel = s => {
    const fp = s.fingerprint || s.stage0_run_id?.slice(0, 8) || '?';
    return `${s.house} — ${s.internal_name} | ${s.period_open || '?'} | #${fp}`;
  };

  // ── Scan ──────────────────────────────────────────────────────────────────
  const handleScan = async () => {
    if (!selectedSource || !selectedSite) { toast.error('Select a source first'); return; }
    setScanning(true);
    setRun(null);
    setApplyResult(null);
    setDryRunResult(null);
    setFindings({ items: [], total: 0 });
    try {
      const r = await api.post('/admin/psb-stage1/scan', {
        stage0_run_id: selectedSource,
        target_site_id: Number(selectedSite),
      });
      setRun({ run_id: r.data.run_id, status: 'scanning' });
      setPolling(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Scan failed to start');
    } finally {
      setScanning(false);
    }
  };

  // ── Poll while scanning ────────────────────────────────────────────────────
  useEffect(() => {
    if (!polling || !run?.run_id) return;
    const interval = setInterval(async () => {
      try {
        const r = await api.get(`/admin/psb-stage1/runs/${run.run_id}`);
        setRun(r.data);
        if (['scanned', 'failed', 'applied'].includes(r.data.status)) {
          setPolling(false);
          if (r.data.status === 'scanned') toast.success('Scan complete');
          if (r.data.status === 'failed') toast.error('Scan failed — see stats.error');
        }
      } catch { /* keep polling */ }
    }, 2500);
    return () => clearInterval(interval);
  }, [polling, run?.run_id]);

  // ── Tab change — atomic filter + category reset (single React event batch) ──
  // All setters below are called synchronously inside one click-handler, so
  // React 18 batches them into a single render.  The findings-fetch effect then
  // fires exactly once with the correct new values.  There is intentionally NO
  // separate category-reset useEffect: a competing effect caused a race where
  // the findings-fetch fired with stale statusFilter/page values, the API
  // returned 0 rows, and the stale-cancel guard discarded the corrective call.
  const handleTabChange = useCallback((newCategory) => {
    setCategory(newCategory);
    setPage(1);
    setStatusFilter('all');
    setSearch('');
    setSelected(new Set());
    // Do NOT call setFindings here — findings-fetch clears and reloads them
    // deterministically once (below) with the correct category in scope.
  }, []);

  // ── Load findings — stale-cancel + run/category guard ─────────────────────
  // Keyed on both run_id and category so a response from a previous tab or a
  // previous run can never overwrite the current view.  The stale flag (set in
  // cleanup) is the primary guard; expectedRunId / expectedCategory are a
  // belt-and-suspenders check inside the async callback closure.
  useEffect(() => {
    if (!run?.run_id || run.status === 'scanning') return;
    let stale = false;
    const expectedRunId = run.run_id;
    const expectedCategory = category;

    setLoadingFindings(true);
    setFindings({ items: [], total: 0 }); // clear stale data before every fetch

    (async () => {
      try {
        const params = { category, page, page_size: 25 };
        if (statusFilter !== 'all') params.status = statusFilter;
        if (search) params.search = search;
        const r = await api.get(
          `/admin/psb-stage1/runs/${run.run_id}/findings`,
          { params },
        );
        // Only commit if this invocation is still current (not superseded)
        if (!stale && run?.run_id === expectedRunId && category === expectedCategory) {
          setFindings(r.data);
          setSelected(new Set());
        }
      } catch (e) {
        if (!stale) toast.error('Failed to load findings');
      } finally {
        if (!stale) setLoadingFindings(false);
      }
    })();

    return () => { stale = true; };
  // findingsVersion lets callers (decisions, dry-run) force a refresh without
  // changing any other dep.
  }, [run?.run_id, run?.status, category, statusFilter, search, page, findingsVersion]);

  // ── Selection helpers ─────────────────────────────────────────────────────
  const toggleSelect = id => {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleSelectAll = () => {
    setSelected(
      selected.size === findings.items.length
        ? new Set()
        : new Set(findings.items.map(it => it.id)),
    );
  };

  // ── Decisions ─────────────────────────────────────────────────────────────
  const handleDecision = async action => {
    if (selected.size === 0) { toast.error('Select at least one row'); return; }
    try {
      await api.post(`/admin/psb-stage1/runs/${run.run_id}/decisions`, {
        category,
        ids: Array.from(selected),
        action,
      });
      toast.success(`${selected.size} row(s) ${action === 'approve' ? 'approved' : 'rejected'}`);
      // Refresh run metadata (has_approvals, approved_count)
      const refreshed = await api.get(`/admin/psb-stage1/runs/${run.run_id}`);
      setRun(refreshed.data);
      setFindingsVersion(v => v + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Decision failed');
    }
  };

  // ── AI opt-in per row ─────────────────────────────────────────────────────
  const handleAiSuggest = async findingId => {
    setAiLoading(prev => new Set(prev).add(findingId));
    try {
      const r = await api.post(
        `/admin/psb-stage1/runs/${run.run_id}/findings/${findingId}/ai-suggest`,
      );
      const suggestion = r.data?.ai_suggestion;
      if (suggestion) {
        // Patch the local row in-place — no need to re-fetch the whole page
        setFindings(prev => ({
          ...prev,
          items: prev.items.map(it =>
            it.id === findingId ? { ...it, ai_suggestion: suggestion } : it,
          ),
        }));
        toast.success('AI suggestion added');
      } else {
        toast.info('No AI suggestion generated for this row');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI suggestion failed');
    } finally {
      setAiLoading(prev => { const n = new Set(prev); n.delete(findingId); return n; });
    }
  };

  // ── Dry-run ───────────────────────────────────────────────────────────────
  const handleDryRun = async () => {
    try {
      const r = await api.post(`/admin/psb-stage1/runs/${run.run_id}/dry-run`);
      setDryRunResult(r.data);
      const refreshed = await api.get(`/admin/psb-stage1/runs/${run.run_id}`);
      setRun(refreshed.data);
      toast.success('Dry-run complete — review the plan below');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Dry-run failed');
    }
  };

  // ── Apply ─────────────────────────────────────────────────────────────────
  const canApply =
    run?.dry_run_at && run?.has_approvals && !applying && !isProduction && run?.status !== 'scanning';

  const applyBlockedReason = !run
    ? null
    : isProduction
      ? 'Apply is blocked in Production'
      : !run.dry_run_at
        ? 'Complete a successful dry-run first'
        : !run.has_approvals
          ? 'Approve at least one finding first'
          : null;

  const handleApply = async () => {
    if (!canApply) { toast.error(applyBlockedReason || 'Apply not available'); return; }
    if (!window.confirm(
      'Apply approved changes to the disposable test fixture? This writes to the database (Preview only).',
    )) return;
    setApplying(true);
    try {
      const r = await api.post(`/admin/psb-stage1/runs/${run.run_id}/apply`, {});
      setApplyResult(r.data);
      toast.success('Apply complete');
      const refreshed = await api.get(`/admin/psb-stage1/runs/${run.run_id}`);
      setRun(refreshed.data);
      setFindingsVersion(v => v + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  const handleExport = () => {
    const base = api.defaults.baseURL || '';
    window.open(`${base}/admin/psb-stage1/runs/${run.run_id}/export?fmt=csv`, '_blank');
  };

  const tabCounts = run?.stats?.tab_counts || {};
  const cols = COL_COUNT[category] || 6;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="psb-stage1-page">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button
            variant="ghost" size="sm" className="gap-1.5 -ml-2 mb-2"
            onClick={() => navigate('/stocktaking/dashboard')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
          <h1 className="text-2xl font-bold text-slate-900">Product Import Scan</h1>
          <p className="text-sm text-slate-500 mt-1">
            Preview only — creates a new read-only scan run. Does not affect the governed product review.
          </p>
        </div>
        {isProduction ? (
          <Badge
            variant="outline"
            className="bg-red-50 text-red-700 border-red-300 px-3 py-1.5"
            data-testid="production-banner"
          >
            <ShieldAlert className="w-4 h-4 mr-1.5" /> PRODUCTION — Apply is blocked
          </Badge>
        ) : (
          <Badge
            variant="outline"
            className="bg-blue-50 text-blue-700 border-blue-200 px-3 py-1.5"
            data-testid="preview-banner"
          >
            PREVIEW environment
          </Badge>
        )}
      </div>

      {/* Callout — route reviewers to the governed matching queue */}
      <div
        className="flex items-start gap-3 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm"
        data-testid="stage1b-callout-banner"
      >
        <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0 text-indigo-500" />
        <div>
          <span className="font-semibold text-indigo-800">This page creates preview scan runs only</span>
          <span className="text-indigo-700"> and does not affect the governed product-matching session.</span>
          <div className="mt-1">
            <a
              href={pinnedRunId
                ? `/stocktaking/psb-stage1b-mapping?s1_run_id=${pinnedRunId}${urlSiteId ? `&site_id=${urlSiteId}` : ''}`
                : '/stocktaking/psb-stage1b-mapping'}
              className="inline-flex items-center gap-1 text-indigo-700 font-medium underline underline-offset-2 hover:text-indigo-900"
              data-testid="stage1b-review-link"
            >
              Go to Review product matches →
            </a>
            {!pinnedRunId && (
              <span className="text-indigo-500 ml-2 text-xs">(select or start a scan run first)</span>
            )}
          </div>
        </div>
      </div>

      {/* Pinned-run isolation error — fail closed, no fallback */}
      {pinnedRunError && (
        <Alert variant="destructive" data-testid="pinned-run-error">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Run isolation failure — no product data has been loaded.</strong>{' '}
            {pinnedRunError}
          </AlertDescription>
        </Alert>
      )}

      {/* Pinned-run identity badge — shown once the run is loaded */}
      {pinnedRunId && run && !pinnedRunError && (
        <div
          className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-800"
          data-testid="pinned-run-identity-banner"
        >
          <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />
          <span>
            Showing{' '}
            <strong>{run.source_house}{run.source_internal_name ? ` — ${run.source_internal_name}` : ''}</strong>
            {run.target_site_name && <> &rarr; <strong>{run.target_site_name}</strong></>}
            {run.stats?.total_source_products != null && (
              <> &middot; {run.stats.total_source_products} products
                {(() => {
                  const rr = run.stats?.by_status?.review_required || 0;
                  const ss = run.stats?.by_status?.strong_suggestion || 0;
                  const needsReview = rr + ss;
                  return needsReview > 0 ? <>, {needsReview} need review</> : null;
                })()}
              </>
            )}
          </span>
        </div>
      )}

      {/* Pinned-run loading spinner */}
      {pinnedRunLoading && (
        <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="pinned-run-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading pinned run…
        </div>
      )}

      {/* Setup — hidden when navigated via a pinned run_id */}
      {!pinnedRunId && (
        <Card data-testid="setup-card">
        <CardHeader>
          <CardTitle className="text-base">1. Select source &amp; target (preview scan only)</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4">
          {/* Source selector */}
          <div className="min-w-[360px]">
            <label className="text-xs font-medium text-slate-500 mb-1 block">
              Decoded Stage 0 source
            </label>
            <Select value={selectedSource} onValueChange={setSelectedSource}>
              <SelectTrigger data-testid="source-select">
                <SelectValue placeholder="Choose a decoded source…" />
              </SelectTrigger>
              <SelectContent>
                {sources.map(s => (
                  <SelectItem
                    key={s.stage0_run_id}
                    value={s.stage0_run_id}
                    data-testid={`source-option-${s.stage0_run_id?.slice(0, 8)}`}
                  >
                    {sourceLabel(s)}
                    {s.tranfile_active != null && (
                      <span className="ml-2 text-slate-400">({s.tranfile_active} active)</span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Target site — locked to allow-list, never a free picker */}
          <div className="min-w-[280px]">
            <label className="text-xs font-medium text-slate-500 mb-1 block">
              Target site (preview scan only)
            </label>
            {sites.length === 0 ? (
              <div className="h-9 px-3 flex items-center text-sm text-slate-400 border rounded-md bg-slate-50">
                Loading…
              </div>
            ) : sites.length === 1 ? (
              <div
                className="h-9 px-3 flex items-center justify-between text-sm border rounded-md bg-slate-50"
                data-testid="site-locked-display"
              >
                <span className="text-slate-700 font-medium">{sites[0].name}</span>
                <span className="flex items-center gap-1 text-xs text-slate-400 ml-2">
                  <Lock className="w-3 h-3" /> {sites[0].currency}
                </span>
              </div>
            ) : (
              <SiteCombobox
                sites={sites}
                value={selectedSite}
                onValueChange={setSelectedSite}
                placeholder="Choose target site…"
                testId="site-select"
                showCurrency
              />
            )}
          </div>

          <Button
            onClick={handleScan}
            disabled={scanning || polling || !selectedSource || !selectedSite}
            data-testid="scan-btn"
            className="gap-1.5"
          >
            {(scanning || polling)
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <PlayCircle className="w-4 h-4" />}
            {polling ? 'Scanning…' : 'Scan'}
          </Button>
        </CardContent>
      </Card>
      )} {/* end !pinnedRunId setup card */}

      {/* Run summary */}
      {run && (
        <Card data-testid="run-summary-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              Run {run.run_id?.slice(0, 8)} — <StatusBadge status={run.status} />
              {run.dry_run_at && (
                <span className="text-xs text-slate-500 font-normal" data-testid="dry-run-timestamp">
                  Dry-run: {new Date(run.dry_run_at).toLocaleTimeString()}
                </span>
              )}
            </CardTitle>
            {(run.status === 'scanned' || run.status === 'applied') && (
              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm" variant="outline" className="gap-1.5"
                  onClick={handleDryRun}
                  data-testid="dry-run-btn"
                >
                  <RefreshCw className="w-4 h-4" /> Dry-run
                </Button>
                <Button
                  size="sm" variant="outline" className="gap-1.5"
                  onClick={handleExport}
                  data-testid="export-btn"
                >
                  <Download className="w-4 h-4" /> Export CSV
                </Button>
                <Button
                  size="sm"
                  className={`gap-1.5 ${!canApply ? 'opacity-50 cursor-not-allowed' : ''}`}
                  onClick={handleApply}
                  disabled={!canApply}
                  title={applyBlockedReason || 'Apply approved changes'}
                  data-testid="apply-btn"
                >
                  {applying
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <CheckCheck className="w-4 h-4" />}
                  Apply
                </Button>
              </div>
            )}
          </CardHeader>

          {applyBlockedReason && run.status !== 'scanning' && run.status !== 'applied' && (
            <div className="px-6 pb-2 text-xs text-amber-600" data-testid="apply-gate-note">
              Apply requires: {applyBlockedReason}
            </div>
          )}

          {run.stats?.by_status && (
            <CardContent className="flex flex-wrap gap-2 pt-0" data-testid="run-stats">
              <Badge variant="outline">Total: {run.stats.total_source_products}</Badge>
              {Object.entries(run.stats.by_status).map(([k, v]) => (
                <Badge key={k} variant="outline" className={STATUS_STYLE[k]}>
                  {k.replace(/_/g, ' ')}: {v}
                </Badge>
              ))}
              <Badge variant="outline">Proposals: {run.stats.new_proposals}</Badge>
              <Badge variant="outline">Post-mix: {run.stats.postmix_exceptions}</Badge>
              <Badge variant="outline">
                Prices: {run.stats.price_mappings} ({run.stats.price_blocked} blocked)
              </Badge>
              {run.has_approvals && (
                <Badge
                  variant="outline"
                  className="bg-emerald-50 text-emerald-700 border-emerald-200"
                  data-testid="approved-count-badge"
                >
                  {run.approved_count} approved
                </Badge>
              )}
            </CardContent>
          )}

          {run.stats?.error && (
            <CardContent>
              <p className="text-sm text-red-600" data-testid="scan-error">{run.stats.error}</p>
            </CardContent>
          )}
        </Card>
      )}

      {/* Dry-run plan */}
      {dryRunResult && (
        <Card data-testid="dry-run-result-card">
          <CardHeader>
            <CardTitle className="text-base">Dry-run plan (no writes)</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            {Object.entries(dryRunResult.counts).map(([cat, c]) => (
              <Badge key={cat} variant="outline" className="px-3 py-1.5">
                {cat}: insert {c.insert} · update {c.update} · skip {c.skip} · blocked {c.blocked}
              </Badge>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Apply result */}
      {applyResult && (
        <Card data-testid="apply-result-card">
          <CardHeader>
            <CardTitle className="text-base">Apply result</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            {Object.entries(applyResult.applied || {}).map(([cat, c]) => (
              <Badge key={cat} variant="outline" className="px-3 py-1.5">{cat}: {c}</Badge>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Findings */}
      {run?.status && run.status !== 'scanning' && (
        <Card data-testid="findings-card">
          <CardHeader className="space-y-3">
            {/* Tab buttons with per-tab counts */}
            <div className="flex flex-wrap gap-2">
              {CATEGORY_TABS.map(t => {
                const cnt = tabCounts[t.countKey];
                return (
                  <Button
                    key={t.key}
                    size="sm"
                    variant={category === t.key ? 'default' : 'outline'}
                    onClick={() => handleTabChange(t.key)}
                    data-testid={`tab-${t.key}`}
                  >
                    {t.label}{cnt != null ? ` (${cnt})` : ''}
                  </Button>
                );
              })}
            </div>

            {/* Filters + bulk actions */}
            <div className="flex flex-wrap items-center gap-3">
              <Select
                value={statusFilter}
                onValueChange={v => { setStatusFilter(v); setPage(1); }}
              >
                <SelectTrigger className="w-[200px]" data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {(STATUS_OPTIONS[category] || []).map(s => (
                    <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="relative w-[260px]">
                <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
                <Input
                  className="pl-8"
                  placeholder="Search code / description"
                  value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1); }}
                  data-testid="search-input"
                />
              </div>

              <div className="flex gap-2 ml-auto">
                <Button
                  size="sm" variant="outline" className="gap-1.5"
                  onClick={() => handleDecision('approve')}
                  data-testid="approve-selected-btn"
                >
                  <CheckCheck className="w-4 h-4" /> Approve ({selected.size})
                </Button>
                <Button
                  size="sm" variant="outline" className="gap-1.5"
                  onClick={() => handleDecision('reject')}
                  data-testid="reject-selected-btn"
                >
                  <X className="w-4 h-4" /> Reject
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardContent className="overflow-x-auto">
            {loadingFindings ? (
              <div
                className="py-12 text-center text-slate-400"
                data-testid="findings-loading"
              >
                <Loader2 className="w-6 h-6 animate-spin mx-auto" />
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="findings-table">
                <thead>
                  <TableHeader
                    category={category}
                    allSelected={selected.size > 0 && selected.size === findings.items.length}
                    onToggleAll={toggleSelectAll}
                  />
                </thead>
                <tbody>
                  {findings.items.map(it => (
                    <TableRow
                      key={it.id}
                      category={category}
                      it={it}
                      selected={selected.has(it.id)}
                      onToggle={toggleSelect}
                      onAiSuggest={handleAiSuggest}
                      aiLoading={aiLoading.has(it.id)}
                    />
                  ))}
                  {findings.items.length === 0 && !loadingFindings && (
                    <tr>
                      <td
                        colSpan={cols}
                        className="py-10 text-center text-slate-400"
                        data-testid="findings-empty"
                      >
                        No findings for this filter
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4 text-xs text-slate-500">
              <span data-testid="findings-total">
                {findings.total} total
                {search && ` matching "${search}"`}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm" variant="outline"
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                  data-testid="prev-page-btn"
                >
                  Prev
                </Button>
                <span className="px-2 py-1" data-testid="page-indicator">
                  {page} / {Math.max(1, Math.ceil(findings.total / 25))}
                </span>
                <Button
                  size="sm" variant="outline"
                  disabled={page * 25 >= findings.total}
                  onClick={() => setPage(p => p + 1)}
                  data-testid="next-page-btn"
                >
                  Next
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
